import type { SVGProps } from 'react';

/**
 * Système d'icônes ASCEND INVICTUS — SVG local, monoline, sans dépendance.
 * Les 9 modes et les 8 familles émotionnelles disposent de glyphes distincts.
 * Port fidèle de design-handoff/.../src/components/core/Icon.jsx : ne pas remplacer
 * ces tracés par une bibliothèque d'icônes générique.
 */
export type IconName =
  | 'normal'
  | 'charge'
  | 'voyage'
  | 'recuperation'
  | 'difficile'
  | 'elevation'
  | 'moment-decisif'
  | 'imprevu'
  | 'transition'
  | 'rayonnement'
  | 'serenite'
  | 'pression-peur'
  | 'friction'
  | 'vulnerabilite'
  | 'reparation'
  | 'recentrage'
  | 'coup-dur'
  | 'mental'
  | 'mouvement'
  | 'focus'
  | 'vie'
  | 'energie'
  | 'joie'
  | 'clarte'
  | 'protection'
  | 'maitrise'
  | 'menu'
  | 'detail'
  | 'fleche'
  | 'derouler'
  | 'replier'
  | 'retour'
  | 'plus'
  | 'reorganiser';

function Glyph({ name }: { readonly name: IconName }) {
  switch (name) {
    case 'normal':
      return (
        <>
          <circle cx="12" cy="12" r="7" />
          <circle cx="12" cy="12" r="2" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
        </>
      );
    case 'charge':
      return (
        <>
          <path d="M13 2 6.5 13h5L10.8 22 18 10h-5z" />
          <path d="M4 5h3M17 19h3" />
        </>
      );
    case 'voyage':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="m15.8 8.2-2.1 5.5-5.5 2.1 2.1-5.5z" />
          <circle cx="12" cy="12" r="1" />
        </>
      );
    case 'recuperation':
      return (
        <>
          <path d="M19 5c-7 .2-11 3.6-11 9.5 0 2.4 1.4 4.5 4 5.5 4.5-1.7 7-5.7 7-15Z" />
          <path d="M5 20c2.5-5.3 6-8.5 11-11" />
        </>
      );
    case 'difficile':
      return (
        <>
          <path d="m3 19 6.2-10 3 4 2.8-6 6 12Z" />
          <path d="M7 19h10" />
        </>
      );
    case 'elevation':
      return (
        <>
          <path d="M5 19h4v-4h4v-4h4V7h2" />
          <path d="m15 5 4-3 3 4" />
        </>
      );
    case 'moment-decisif':
      return (
        <>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 2v5M12 17v5M2 12h5M17 12h5" />
          <path d="m9 12 2 2 4-5" />
        </>
      );
    case 'imprevu':
      return (
        <>
          <path d="M4 6h5c4 0 3 5 7 5h4" />
          <path d="m17 8 3 3-3 3" />
          <path d="M4 18h5c3 0 3-3 5-4" />
        </>
      );
    case 'transition':
      return (
        <>
          <path d="M5 20V4h8v16" />
          <path d="M13 7h6v10h-6" />
          <path d="m9 12 2-2m-2 2 2 2" />
        </>
      );
    case 'rayonnement':
      return (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
        </>
      );
    case 'serenite':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M6 12c2-2 4-2 6 0s4 2 6 0M8 16h8" />
        </>
      );
    case 'pression-peur':
      return (
        <>
          <path d="M12 3 4 7v5c0 5 3.3 8 8 9 4.7-1 8-4 8-9V7z" />
          <path d="M12 8v5M12 16h.01" />
        </>
      );
    case 'friction':
      return (
        <>
          <path d="m5 5 6 6M13 13l6 6M19 5l-5 5M10 14l-5 5" />
          <path d="M9 3l2 4M15 17l2 4" />
        </>
      );
    case 'vulnerabilite':
      return (
        <>
          <path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.6-7 10-7 10Z" />
          <path d="M9.5 12.5 12 15l2.5-3" />
        </>
      );
    case 'reparation':
      return (
        <>
          <path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.6-7 10-7 10Z" />
          <path d="M8 12h2l1-3 2 6 1-3h2" />
        </>
      );
    case 'recentrage':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4" />
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        </>
      );
    case 'coup-dur':
      return (
        <>
          <path d="M12 3 4 7v5c0 5 3.3 8 8 9 4.7-1 8-4 8-9V7z" />
          <path d="m9 9 6 6M15 9l-6 6" />
        </>
      );
    case 'mental':
      return (
        <>
          <path d="M9 5a3 3 0 0 0-5 2.2A3 3 0 0 0 5 12a3 3 0 0 0 4 4.5V5ZM15 5a3 3 0 0 1 5 2.2A3 3 0 0 1 19 12a3 3 0 0 1-4 4.5V5Z" />
          <path d="M9 9H7M15 9h2M9 14H7M15 14h2" />
        </>
      );
    case 'mouvement':
      return (
        <>
          <circle cx="12" cy="5" r="2" />
          <path d="m10 9-3 4 3 2-2 5M14 9l3 3-2 3 2 5M10 9l4 1" />
        </>
      );
    case 'focus':
      return (
        <>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
        </>
      );
    case 'vie':
      return (
        <>
          <path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.6-7 10-7 10Z" />
          <path d="M8 13h2l1-2 2 4 1-2h2" />
        </>
      );
    case 'energie':
      return <path d="M13 2 6.5 13h5L10.8 22 18 10h-5z" />;
    case 'joie':
      return (
        <>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
        </>
      );
    case 'clarte':
      return (
        <>
          <path d="M12 3a7 7 0 0 0-4 12.7V19h8v-3.3A7 7 0 0 0 12 3Z" />
          <path d="M9 22h6M9 11h6" />
        </>
      );
    case 'protection':
      return (
        <>
          <path d="M12 3 4 7v5c0 5 3.3 8 8 9 4.7-1 8-4 8-9V7z" />
          <path d="M9 12h6M12 9v6" />
        </>
      );
    case 'maitrise':
      return (
        <>
          <path d="m6 15 6-6 6 6" />
          <path d="m6 20 6-6 6 6" />
        </>
      );
    case 'menu':
      return <path d="M4 7h16M4 12h16M4 17h16" />;
    case 'detail':
    case 'fleche':
      return <path d="m9 5 7 7-7 7" />;
    case 'derouler':
      return <path d="m5 9 7 7 7-7" />;
    case 'replier':
      return <path d="m5 15 7-7 7 7" />;
    case 'retour':
      return <path d="m15 5-7 7 7 7" />;
    case 'plus':
      return <path d="M12 5v14M5 12h14" />;
    case 'reorganiser':
      return (
        <>
          <path d="M4 6h11M4 12h8M4 18h11" />
          <path d="m16 10 4 4-4 4M20 14h-7" />
        </>
      );
    default:
      return <circle cx="12" cy="12" r="8" />;
  }
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'strokeWidth'> {
  readonly name?: IconName;
  readonly size?: number;
  readonly strokeWidth?: number | 'light';
  readonly title?: string;
  readonly decorative?: boolean;
}

export function Icon({
  name = 'normal',
  size = 18,
  strokeWidth = 1.6,
  style,
  title,
  decorative,
  ...rest
}: IconProps) {
  const labelled = Boolean(title) && decorative !== true;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={typeof strokeWidth === 'number' ? strokeWidth : strokeWidth === 'light' ? 1.35 : 1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? title : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
      {...rest}
      style={{ display: 'block', flex: '0 0 auto', ...style }}
    >
      {labelled && <title>{title}</title>}
      <Glyph name={name} />
    </svg>
  );
}
